import { Link } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { DrilldownLink } from '../drilldowns';

/**
 * Generic ranked-list tile body. Each row is a single horizontal line:
 *   `[ label ........... bar ............ value ]`
 * Three columns share one row so the bar reads as the row's data,
 * the label as its category, and the value as the count — same
 * visual rhythm as the design mock for `open_tasks_by_age`.
 *
 * Used by `tasks_by_assignee` and `open_tasks_by_age`. Each row can
 * carry an optional `drilldown` link — the entire row becomes a
 * `<Link>` so a click anywhere on the row navigates to the filtered
 * list page.
 */
export interface ListTileRow {
  id: string;
  label: string;
  value: number;
  secondary?: string;
  drilldown?: DrilldownLink;
  /**
   * Per-row visual tone for the bar fill. `warning` recolors the bar
   * to amber — used by `open_tasks_by_age` to flag stale buckets
   * (>= 3 days) at a glance. The row with tone='warning' AND the
   * largest value also picks up red text on its number, calling out
   * the worst offender.
   */
  tone?: 'default' | 'warning';
}

export function ListTile({
  rows,
  isLoading,
  isError,
  emptyMessage = 'No data',
}: {
  rows: ListTileRow[];
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
}): React.ReactElement {
  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Failed to load
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  // Bar fill is normalized to the largest row in the list — relative
  // ranking is the visual goal here, not absolute units.
  const max = Math.max(...rows.map((r) => r.value), 1);
  // Pick the worst-performing row: largest among rows tagged `warning`.
  // Its value renders in red so the manager's eye lands there first.
  const worstWarningId = (() => {
    let bestVal = -1;
    let id: string | null = null;
    for (const r of rows) {
      if (r.tone === 'warning' && r.value > bestVal) {
        bestVal = r.value;
        id = r.id;
      }
    }
    return id;
  })();

  return (
    <ul className="flex h-full flex-col gap-2.5">
      {rows.map((r) => {
        const pct = (r.value / max) * 100;
        const isWorst = r.id === worstWarningId;
        const body = (
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,5fr)_auto] items-center gap-3">
            {/* Label — same body family/size used elsewhere on the
                dashboard (no custom letter-spacing). Truncates when the
                tile is narrow. */}
            <span className="truncate text-sm font-medium">{r.label}</span>
            {/* Bar — fills the middle column. Amber for warning rows;
                primary-tinted for healthy. Hover deepens the fill so the
                row reads as clickable when a drilldown is attached. */}
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted/60">
              <div
                className={cn(
                  'h-full rounded-full transition-[width]',
                  r.tone === 'warning' ? 'bg-warning/70' : 'bg-primary/50'
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            {/* Value — same body size as the label. Worst-performing
                warning row gets red text to call it out. Secondary
                metric (if any) precedes the count, separated by ·. */}
            <span
              className={cn(
                'shrink-0 text-sm tabular-nums',
                isWorst ? 'font-medium text-destructive' : 'text-muted-foreground'
              )}
            >
              {r.secondary ? `${r.secondary} · ` : ''}
              {r.value.toLocaleString()}
            </span>
          </div>
        );
        return (
          <li key={r.id}>
            {r.drilldown ? (
              <Link
                to={r.drilldown.to}
                search={r.drilldown.search}
                title={r.drilldown.title}
                className="block rounded-sm transition-colors hover:[&_div:nth-child(2)>div]:bg-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {body}
              </Link>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}
